import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import { getStarterUpgradeProduct } from 'pricing-pages-lib/utils/getUpgradeProduct';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import BannerSection from '../components/banners/BannerSection';
import { CreditsCalloutSection } from '../components/common/credits-callout/CreditsCalloutSection';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import IntegrationsSection from '../components/common/IntegrationsSection';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import PhoneSupportMiddle from '../components/common/PhoneSupportMiddle';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import SuiteFeatureTable from '../components/feature-table/suite-feature-table/SuiteFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { SuitePageCardContainer } from '../components/merch-card/card-containers/SuitePageCardContainer';
import { SUITE_PAGE, SUITE_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT, OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageParams } from '../hooks/usePageParams';
import { isUngatedForInbound25Selector } from '../selectors/GatingSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SuitePage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('crmSuite');
  const screen = SCREENS.SUITE;
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const isUngatedForInbound25 = useSelector(isUngatedForInbound25Selector);
  const {
    tab
  } = usePageParams();
  const upgradeProduct = getStarterUpgradeProduct({
    productLine: undefined,
    tab
  });
  useEffect(() => {
    trackPageView({
      screen,
      tab
    });
  }, [screen, tab]);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const marketingHighlightedProducts = getHighlightsForProductLine(ProductLines.MARKETING);
  const filteredMarketingHighlightedProducts = marketingHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const salesHighlightedProducts = getHighlightsForProductLine(ProductLines.SALES);
  const filteredSalesHighlightedProducts = salesHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const serviceHighlightedProducts = getHighlightsForProductLine(ProductLines.SERVICE);
  const filteredServiceHighlightedProducts = serviceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const operationsHighlightedProducts = getHighlightsForProductLine(ProductLines.OPERATIONS);
  const filteredOperationsHighlightedProducts = operationsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const cmsHighlightedProducts = getHighlightsForProductLine(ProductLines.CMS);
  const filteredCmsHighlightedProducts = cmsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const commerceHighlightedProducts = getHighlightsForProductLine(ProductLines.COMMERCE);
  const filteredCommerceHighlightedProducts = commerceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const productsByProductLine = {
    [ProductLines.MARKETING]: filteredMarketingHighlightedProducts,
    [ProductLines.SALES]: filteredSalesHighlightedProducts,
    [ProductLines.SERVICE]: filteredServiceHighlightedProducts,
    [ProductLines.OPERATIONS]: filteredOperationsHighlightedProducts,
    [ProductLines.CMS]: filteredCmsHighlightedProducts,
    [ProductLines.COMMERCE]: filteredCommerceHighlightedProducts
  };
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: NavItemEnum.SUITE
    }),
    errorBoundaryName: SUITE_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: "SUITE_PAGE"
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: NavItemEnum.SUITE,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            screen: screen
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              nonProductIdentifier: NavItemEnum.SUITE
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              nonProductIdentifier: NavItemEnum.SUITE
            })]
          }), /*#__PURE__*/_jsx(SuitePageCardContainer, {
            selectedTab: tab
          }), isUngatedForInbound25 && /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(CreditsCalloutSection, {
              navItem: NavItemEnum.SUITE
            })
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(SuiteFeatureTable, {
              productsByProductLine: productsByProductLine,
              screen: screen
            }, tab)
          }), /*#__PURE__*/_jsx(PhoneSupportMiddle, {
            phoneSourceId: "crmsuite-middle",
            screen: screen,
            upgradeProduct: upgradeProduct
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(Faq, {
            children: FaqsConfig[NavItemEnum.SUITE].map(_ref => {
              let {
                  questionKey
                } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            })
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: "crmsuite-footer",
            screen: screen,
            upgradeProduct: upgradeProduct
          })]
        })
      })]
    })
  });
};
export default withErrorBoundary(SuitePage, {
  errorBoundaryName: SUITE_PAGE,
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  severity: ERROR_SEVERITY.HIGH
});